import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';

export interface ReportsSummary {
  period: { from: string; to: string };
  screenings: {
    submitted: number;
    uploaded: number;
    pending: number;
    flagged: number;
    noMedicaid: number;
    uploadedPercent: number;
    noMedicaidPercent: number;
  };
  agents: {
    activeCount: number;
    totalScreenings: number;
    avgScreeningsPerAgent: number;
    topAgent: { id: number; name: string; count: number } | null;
  };
  uploaders: {
    activeCount: number;
    uploadedToday: number;
    avgTimeToUploadHours: number;
    slaBreaches24h: number;
  };
  bookings: {
    scheduled: number;
    completed: number;
    missed: number;
    missedPercent: number;
  };
  household: {
    captured: number;
    withMedicaidConfirmed: number;
    withoutMedicaid: number;
    stillPending: number;
  };
  callAttempts: {
    total: number;
    successful: number;
    byOutcome: Array<{ outcome: string; count: number }>;
  };
}

export interface TrendsResponse {
  metric: 'screenings' | 'uploads' | 'bookings' | 'household';
  period: number;
  data: Array<Record<string, string | number>>;
}

export interface ByAgentRow {
  agentId: number;
  agentName: string;
  screeningsSubmitted: number;
  screeningsUploaded: number;
  uploadRate: number;
  callAttempts: number;
  avgCallSeconds: number;
  missedBookings: number;
  householdMembersCaptured: number;
  activeWorkSeconds: number;
  callTimeSeconds: number;
  tier: 'bronze' | 'silver' | 'gold' | 'below_bronze';
}

export interface ByUploaderRow {
  uploaderId: number;
  uploaderName: string;
  uploaded: number;
  flagged: number;
  noMedicaid: number;
  avgTimeToUploadHours: number;
  slaBreaches: number;
}

function qs(obj: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '') parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

export function useReportsSummary(from: string, to: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'summary', from, to],
    queryFn: () => apiCall<ReportsSummary>(`/admin/reports/summary${qs({ from, to })}`),
    staleTime: 60_000,
  });
}

export function useReportsTrends(metric: TrendsResponse['metric'], period: number) {
  return useQuery({
    queryKey: ['admin', 'reports', 'trends', metric, period],
    queryFn: () => apiCall<TrendsResponse>(`/admin/reports/trends${qs({ metric, period })}`),
    staleTime: 60_000,
  });
}

export function useReportsByAgent(from: string, to: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'by-agent', from, to],
    queryFn: () => apiCall<ByAgentRow[]>(`/admin/reports/by-agent${qs({ from, to })}`),
    staleTime: 60_000,
  });
}

export function useReportsByUploader(from: string, to: string) {
  return useQuery({
    queryKey: ['admin', 'reports', 'by-uploader', from, to],
    queryFn: () => apiCall<ByUploaderRow[]>(`/admin/reports/by-uploader${qs({ from, to })}`),
    staleTime: 60_000,
  });
}

// A-14 (2026-05-14): booking completion KPIs widget.
export interface BookingKpis {
  windowDays: number;
  completed: number;
  missed: number;
  cancelled: number;
  scheduled: number;
  completionRate: number;
  noShowRate: number;
}

export function useBookingKpis(windowDays: number = 30) {
  return useQuery({
    queryKey: ['admin', 'reports', 'booking-kpis', windowDays],
    queryFn: () => apiCall<BookingKpis>(`/admin/reports/booking-kpis${qs({ window: windowDays })}`),
    staleTime: 60_000,
  });
}

// ============================================================
// Cluster H — Household promotion stats (KPI endpoint shipped by Agent A)
// ============================================================

export interface HouseholdPromotionStats {
  windowDays: number;
  asOf: string;
  totalCapturedHouseholdMembers: number;
  totalPromotedAllTime: number;
  promotedInWindow: number;
  pendingPromotion: number;
  pendingAdultsInBacklog: number | null;
  pendingMinorsInBacklog: number | null;
  promotionRate: number;
  subScreeningsOpenedInWindow: number;
  subScreeningsSubmittedInWindow: number;
  subScreeningsAbandonedInWindow: number;
  subSubmissionCompletionRate: number;
  averageSubScreeningDurationSec: number;
  estimatedRevenueInWindowUsd: number;
  revenuePerSubScreeningUsd: number;
  topAgentsByPromotion: Array<{
    userId: number;
    email: string;
    promotedCount: number;
    subSubmittedCount: number;
    subAbandonedCount: number;
    avgSubDurationSec: number;
    estimatedRevenueUsd: number;
  }>;
  pendingBacklogByCapturingAgent: Array<{
    userId: number;
    email: string;
    capturedTotal: number;
    promotedTotal: number;
    pendingAdults: number | null;
    pendingMinors: number | null;
    promotionRate: number;
    pendingTotal: number;
  }>;
}

export function useHouseholdPromotionStats(windowDays: number = 30, agentId: number | null = null) {
  return useQuery({
    queryKey: ['admin', 'reports', 'household-promotion-stats', windowDays, agentId] as const,
    queryFn: () =>
      apiCall<HouseholdPromotionStats>(
        `/admin/reports/household-promotion-stats${qs({
          window: windowDays,
          ...(agentId !== null ? { agent_id: agentId } : {}),
        })}`,
      ),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}



// ============================================================
// SUB-K3 (v0.4.1) — uploaders snapshot for LiveMonitoring widget
// ============================================================
export interface UploadersBreakdownRow {
  uploaderId: string;
  uploaderName: string;
  uploadedToday: number;
  noMedicaidToday: number;
  flaggedToday: number;
  claimActive: number;
}

export interface UploadersBreakdown {
  perUploader: UploadersBreakdownRow[];
  globalPending: number;
  globalFlagged: number;
}

/**
 * Snapshot of active uploaders + their today productivity + current claims.
 * Consumed by:
 *   - SUB-K1 page /admin/uploaders-activity (drill-down)
 *   - SUB-K3 LiveMonitoring widget (Uploaders activos card)
 *
 * Polls every 30s by default. Pass refetchMs=0 to disable polling.
 */
export function useUploadersBreakdown(refetchMs: number = 30_000) {
  return useQuery({
    queryKey: ['admin', 'reports', 'uploaders-breakdown'] as const,
    queryFn: () => apiCall<UploadersBreakdown>('/admin/reports/uploaders-breakdown'),
    staleTime: 15_000,
    refetchInterval: refetchMs === 0 ? false : refetchMs,
  });
}

/**
 * Trigger a CSV download. Uses fetch with auth header and a blob URL so the
 * browser saves with the right filename.
 */
export async function downloadReportCsv(
  type: 'summary' | 'by-agent' | 'by-uploader',
  from: string,
  to: string,
  accessToken: string,
): Promise<void> {
  const url = `/api/admin/reports/export${qs({ format: 'csv', type, from, to })}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `pathway-report-${type}-${from}-${to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

export interface CallOutcomeExportRow {
  client_id: number;
  outcome: string;
  attempt_at: string;
  agent_id: number;
  agent_name: string;
  assignment_id: number;
  attempt_count: number;
  phone_last4: string;
  client_status: string;
}

export interface CallOutcomePreviewResponse {
  period: { from: string; to: string; timezone: 'America/New_York' };
  presets: { core: boolean; reach: boolean; exhausted: boolean };
  maxCallAttempts: number;
  total: number;
  returned: number;
  truncated: boolean;
  cap: number;
  previewLimit: number;
  rows: CallOutcomeExportRow[];
}

export interface CallOutcomeExportFilters {
  from: string;
  to: string;
  core: boolean;
  reach: boolean;
  exhausted: boolean;
}

function outcomesQs(f: CallOutcomeExportFilters): string {
  const parts = [
    `from=${encodeURIComponent(f.from)}`,
    `to=${encodeURIComponent(f.to)}`,
    `core=${f.core ? '1' : '0'}`,
    `reach=${f.reach ? '1' : '0'}`,
    `exhausted=${f.exhausted ? '1' : '0'}`,
  ];
  return '?' + parts.join('&');
}

export function previewCallOutcomes(
  filters: CallOutcomeExportFilters,
): Promise<CallOutcomePreviewResponse> {
  return apiCall<CallOutcomePreviewResponse>(
    `/admin/reports/call-outcomes/preview${outcomesQs(filters)}`,
  );
}

export async function downloadCallOutcomesCsv(
  filters: CallOutcomeExportFilters,
  accessToken: string,
): Promise<void> {
  const url = `/api/admin/reports/call-outcomes/download${outcomesQs(filters)}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `call-outcomes-${filters.from}-${filters.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}
